import { apiClient } from './api';
import {
  IncubationSession,
  SessionParametersModel,
  WorkflowStepResult,
  WorkflowStepType,
} from '../models/incubation';

export class IncubationService {
  // Create a new incubation session
  static async createSession(params: SessionParametersModel): Promise<IncubationSession> {
    const response = await apiClient.post('/api/incubation/session/create', params);
    return response.data;
  }

  // Start a new incubation workflow session 
  static async startWorkflowSession(originalAsk: string, generateInitialNarrative: boolean = true, maxTokens: number = 256): Promise<any> {
    const response = await apiClient.post('/api/incubation/workflow/start-session', {
      originalAsk,
      generateInitialNarrative,
      maxTokens
    });
    return response.data;
  }

  // Get session by ID
  static async getSession(sessionId: string): Promise<IncubationSession> {
    const response = await apiClient.get(`/api/incubation/session/${sessionId}`);
    return response.data;
  }

  // Execute workflow step
  static async executeWorkflowStep(
    sessionId: string,
    stepType: string,
    maxTokens: number = 1024
  ): Promise<any> {
    const response = await apiClient.post(`/api/incubation/workflow/execute-step/${sessionId}`, {
      stepType,
      maxTokens
    });
    return response.data;
  }

  // Export session
  static async exportSession(sessionId: string, format: string): Promise<Blob> {
    const response = await apiClient.get(
      `/api/incubation/export/${sessionId}?format=${format}`,
      {
        responseType: 'blob',
      }
    );
    return response.data;
  }

  // Save session to local storage
  static saveToLocalStorage(session: IncubationSession): void {
    try {
      const sessions = this.getFromLocalStorage();
      const existingIndex = sessions.findIndex((s) => s.id === session.id);
      
      if (existingIndex >= 0) {
        sessions[existingIndex] = session;
      } else {
        sessions.unshift(session);
      }
      
      // Keep only the last 10 sessions
      const recentSessions = sessions.slice(0, 10);
      localStorage.setItem('incubation_sessions', JSON.stringify(recentSessions));
    } catch (error) {
      console.error('Error saving to local storage:', error);
    }
  }

  // Get sessions from local storage
  static getFromLocalStorage(): IncubationSession[] {
    try {
      const data = localStorage.getItem('incubation_sessions');
      if (data) {
        return JSON.parse(data).map((session: any) => ({
          ...session,
          createdAt: new Date(session.createdAt),
          lastUpdatedAt: new Date(session.lastUpdatedAt),
          messages: session.messages.map((msg: any) => ({
            ...msg,
            timestamp: new Date(msg.timestamp),
          })),
        }));
      }
    } catch (error) {
      console.error('Error loading from local storage:', error);
    }
    return [];
  }

  // Helper function to download file
  static downloadFile(blob: Blob, filename: string): void {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  }
}